import {DEFAULT_ITEM_SORT, ITEM_SORTS, type ItemSort} from '@/api/items';
import {isFlagFilter, type FlagFilter} from '@/lib/flagFilter';
import {DEFAULT_ITEM_FILTERS, sanitizeItemFilters, type ItemFilterState} from '@/store/uiStore';

// The Filters menu fields Content uses. Not "New items only" (Content has no scope for it) or the
// per-investigation sort, which Content keeps in the URL instead.
type ContentFilters = Omit<ItemFilterState, 'newOnlyFilter' | 'sortOrder'>;

// Everything that decides which items Content lists, in the form a saved view stores: only what
// differs from the defaults, so an unchanged control isn't pinned and two views compare as equal
// whenever they show the same thing.
export type ContentViewQuery = {
  q?: string;
  flag?: FlagFilter[];
  sort?: ItemSort;
  platform?: string;
  affiliate?: string;
  filters?: Partial<ContentFilters>;
};

export type ContentViewState = {
  q: string;
  flags: readonly FlagFilter[];
  sort: ItemSort;
  platform: string | null;
  affiliate: string | null;
  filters: ItemFilterState;
};

const FILTER_KEYS = (Object.keys(DEFAULT_ITEM_FILTERS) as Array<keyof ItemFilterState>).filter(
  (key): key is keyof ContentFilters => key !== 'newOnlyFilter' && key !== 'sortOrder'
);

// A view built in to every analyst's menu. Ids are not uuids, so they can never collide with a saved one.
export type BuiltInView = {id: string; name: string; query: ContentViewQuery};

export const BUILT_IN_CONTENT_VIEWS: readonly BuiltInView[] = [
  {id: 'builtin:all', name: 'All content', query: {}},
  // Content narrowed to the caller's own flags, newest flagged first.
  {id: 'builtin:my-flagged', name: 'My flagged items', query: {flag: ['any'], sort: 'flagged_desc'}}
];

export function toContentViewQuery(state: ContentViewState): ContentViewQuery {
  const query: ContentViewQuery = {};
  if (state.q) {
    query.q = state.q;
  }

  if (state.flags.length > 0) {
    query.flag = [...state.flags];
  }

  if (state.sort !== DEFAULT_ITEM_SORT) {
    query.sort = state.sort;
  }

  if (state.platform) {
    query.platform = state.platform;
  }

  if (state.affiliate) {
    query.affiliate = state.affiliate;
  }

  const filters: Partial<ContentFilters> = {};
  for (const key of FILTER_KEYS) {
    if (JSON.stringify(state.filters[key]) !== JSON.stringify(DEFAULT_ITEM_FILTERS[key])) {
      Object.assign(filters, {[key]: state.filters[key]});
    }
  }

  if (Object.keys(filters).length > 0) {
    query.filters = filters;
  }

  return query;
}

// Reads a stored query back defensively: a filter removed since the view was saved, or a value that
// no longer validates, is dropped rather than sent to the items query (where it could 400 the page).
export function parseContentViewQuery(raw: unknown): ContentViewQuery {
  const source = isRecord(raw) ? raw : {};
  const flags = Array.isArray(source.flag)
    ? source.flag.filter((f): f is string => typeof f === 'string').filter(isFlagFilter)
    : [];
  const sort =
    typeof source.sort === 'string' && (ITEM_SORTS as readonly string[]).includes(source.sort)
      ? (source.sort as ItemSort)
      : DEFAULT_ITEM_SORT;
  const storedFilters = isRecord(source.filters) ? source.filters : {};
  const known = Object.fromEntries(
    Object.entries(storedFilters).filter(([key]) =>
      (FILTER_KEYS as readonly string[]).includes(key)
    )
  );

  return toContentViewQuery({
    q: typeof source.q === 'string' ? source.q : '',
    flags,
    sort,
    platform: typeof source.platform === 'string' ? source.platform : null,
    affiliate: typeof source.affiliate === 'string' ? source.affiliate : null,
    filters: sanitizeItemFilters({...DEFAULT_ITEM_FILTERS, ...known})
  });
}

// The full Filters menu state a view puts in place: its own values over the defaults, so applying a
// view clears whatever the previous one set.
export function contentViewFilters(query: ContentViewQuery): ItemFilterState {
  return sanitizeItemFilters({...DEFAULT_ITEM_FILTERS, ...query.filters});
}

// Same keys in the same order on both sides (toContentViewQuery builds them that way), so this is
// a structural comparison.
export function sameContentView(a: ContentViewQuery, b: ContentViewQuery): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
